import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridToolbarContainer,
} from "@mui/x-data-grid"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/DeleteOutlined"
import CancelIcon from "@mui/icons-material/Close"
import { Box, Button, Tooltip } from "@mui/material"
import { useState } from "react"
import DeleteDialog from "./DeleteDialog"
import { GridCellParams } from "@mui/x-data-grid/models/params/gridCellParams"

interface EditToolbarProps<T> {
  data: T
  setData: (data: T[]) => void
  setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void
  emptyObject: T
  fieldToFocus: string
}

interface IEditableTableProps<T> {
  data: T[]
  setData: (data: T[]) => void
  columns: GridColDef[]
  emptyObject: T
  fieldToFocus: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  isCellEditable?: (params: GridCellParams<any, any>) => boolean
  isEditable?: boolean
  onRowUpdate?: (row: T) => void
  shouldDeleteRow?: (row: T) => boolean
  onDelete?: (id: GridRowId | null) => void
  idPropName?: string
  isDeleteRowDisabled?: (row: T) => boolean
}

export default function EditableTable({
  data = [],
  setData = () => {},
  columns = [],
  emptyObject = {},
  fieldToFocus = "",
  isCellEditable = undefined,
  isEditable = true,
  onRowUpdate,
  shouldDeleteRow,
  onDelete,
  idPropName = "id",
  isDeleteRowDisabled,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
IEditableTableProps<any>) {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idForDeletion, setIdForDeletion] = useState<null | GridRowId>(null)
  const [idCounter, setIdCounter] = useState(-1)

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    setIdForDeletion(id)
    setIsDeleteOpen(true)
  }

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })

    const editedRow = data.find((row) => row[idPropName] === id)
    if (editedRow?.[idPropName] < 0) {
      setData(data.filter((row) => row[idPropName] !== id))
    }
  }

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow }

    if (shouldDeleteRow?.(updatedRow)) {
      setData(data.filter((row) => row[idPropName] !== newRow[idPropName]))
      return { ...updatedRow, _action: "delete" }
    }

    setData(data.map((row) => (row[idPropName] === newRow[idPropName] ? updatedRow : row)))
    onRowUpdate?.(updatedRow)
    return updatedRow
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  function EditToolbar(props: EditToolbarProps<any>) {
    const { data, setData, setRowModesModel, emptyObject, fieldToFocus } = props

    const handleClick = () => {
      setData([{ ...emptyObject, [idPropName]: idCounter }, ...data])
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [idCounter]: { mode: GridRowModes.Edit, fieldToFocus: fieldToFocus },
      }))
      setIdCounter(idCounter - 1)
    }

    return (
      <GridToolbarContainer>
        <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
          Add record
        </Button>
      </GridToolbarContainer>
    )
  }

  return (
    <Box className={"editable-table-container"}>
      <DataGrid
        autoHeight
        rows={data}
        columns={[
          ...columns,
          {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            headerClassName: "table-header-cell",
            width: 160,
            getActions: ({ id, row }: GridRowParams) => {
              const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit

              if (isInEditMode && isEditable) {
                return [
                  // eslint-disable-next-line react/jsx-key -- fix it when you will have a chance to check this component
                  <GridActionsCellItem
                    icon={
                      <Tooltip title="Cancel">
                        <CancelIcon />
                      </Tooltip>
                    }
                    label="Cancel"
                    className="textPrimary"
                    onClick={handleCancelClick(id)}
                    color="inherit"
                  />,
                ]
              }

              const resultActions = []

              if (isEditable) {
                resultActions.push(
                  <GridActionsCellItem
                    icon={
                      <Tooltip title="Edit">
                        <EditIcon />
                      </Tooltip>
                    }
                    label="Edit"
                    className="textPrimary"
                    onClick={handleEditClick(id)}
                    color="inherit"
                  />,
                )
              }

              resultActions.push(
                <GridActionsCellItem
                  icon={
                    <Tooltip title="Delete">
                      <DeleteIcon />
                    </Tooltip>
                  }
                  label="Delete"
                  onClick={handleDeleteClick(id)}
                  color="inherit"
                  disabled={isDeleteRowDisabled?.(row) ?? false}
                />,
              )

              return resultActions
            },
          },
        ]}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={processRowUpdate}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 20]}
        slots={{
          toolbar: EditToolbar,
        }}
        density={"compact"}
        slotProps={{
          toolbar: {
            data,
            setData,
            setRowModesModel,
            emptyObject,
            fieldToFocus,
          },
        }}
        getRowId={(row) => row[idPropName]}
        isCellEditable={isCellEditable}
      />
      <DeleteDialog
        open={isDeleteOpen}
        setOpen={setIsDeleteOpen}
        onDeleteClick={() => {
          setData(data.filter((row) => row[idPropName] !== idForDeletion))
          setIsDeleteOpen(false)
          onDelete?.(idForDeletion)
        }}
      />
    </Box>
  )
}
